import React from 'react';
import type { IconType } from 'react-icons';

export type StatusBadgeVariant =
  | 'info'
  | 'success'
  | 'warning'
  | 'error'
  | 'neutral';

export interface StatusBadgeProps {
  variant: StatusBadgeVariant;
  /** sm: compact pill (default) | md: roomier pill for detail views */
  size?: 'sm' | 'md';
  /** Optional leading icon */
  Icon?: IconType;
  children: React.ReactNode;
  className?: string;
}

// Full literal class strings — keep static so Tailwind's purge sees them.
const variantClasses: Record<StatusBadgeVariant, string> = {
  info: 'bg-primary-50 text-primary-700 ring-primary-300 dark:bg-primary-900/40 dark:text-primary-100 dark:ring-primary-700',
  success:
    'bg-success-100 text-success-700 ring-success-300 dark:bg-success-900/40 dark:text-success-200 dark:ring-success-700',
  warning:
    'bg-secondary-light text-secondary-dark ring-secondary-main/60 dark:bg-secondary-dark/30 dark:text-secondary-light dark:ring-secondary-dark',
  error:
    'bg-error-100 text-error-700 ring-error-300 dark:bg-error-900/40 dark:text-error-200 dark:ring-error-700',
  neutral:
    'bg-gray-100 text-gray-700 ring-gray-300 dark:bg-gray-800 dark:text-gray-200 dark:ring-gray-600',
};

const sizeClasses: Record<NonNullable<StatusBadgeProps['size']>, string> = {
  sm: 'px-2 py-0.5 text-xs',
  md: 'px-2.5 py-1 text-sm',
};

/**
 * Semantic status pill. Use the variant that matches the state's meaning
 * (info = informational / scheduled, success = done / positive,
 * warning = in progress / attention, error = failed / destructive,
 * neutral = inactive / unknown). Always pair color with a text label.
 */
export const StatusBadge: React.FC<StatusBadgeProps> = ({
  variant,
  size = 'sm',
  Icon,
  children,
  className = '',
}) => {
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full font-medium ring-1 ring-inset ${variantClasses[variant]} ${sizeClasses[size]} ${className}`.trim()}
    >
      {Icon && <Icon className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />}
      {children}
    </span>
  );
};
